import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { RateMachine } from "@trackmark/core";
import { RatingStrip } from "../design/RatingStrip.js";
import {
  OUTBOX_AUTH_ERROR,
  OUTBOX_ERROR,
  Outbox,
  type OutboxState,
  outboxDroppedMessage,
} from "../rate/outbox.js";
import { boothWrites } from "../rate/writes.js";
import styles from "./Booth.module.css";

export interface QueuedSong {
  id: number;
  title: string;
  artist: string;
  album: string;
  albumId: number;
  year: number | null;
  genre: string | null;
  durationMs: number | null;
  skipCount: number;
  favourite?: boolean;
  art: string | null;
  rating: number | null;
  ratedAt: string | null;
}

export interface BoothProps {
  songs: QueuedSong[];
  onExit: () => void;
  /** Rendered on the empty state — the one place the triage queue is worth advertising. */
  pending?: number;
  onTriage?: () => void;
  /** Injected in tests; the real one talks to /api. */
  outbox?: Outbox;
}

const duration = (ms: number | null) =>
  ms === null ? null : `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}`;

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

/**
 * `02 SEP 24`, formatted explicitly rather than through toLocaleDateString — the design specifies
 * this copy exactly, and ICU renders September as "Sept" in some en-GB builds.
 */
const dateLabel = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getUTCDate()).padStart(2, "0")} ${MONTHS[d.getUTCMonth()]} ${String(d.getUTCFullYear() % 100).padStart(2, "0")}`;
};

/**
 * The rate screen. Entered with `R`, left with `Esc`. The rail stays; the page chrome goes.
 *
 * Nothing in the input path awaits the network: a keypress paints the fill and appends to the
 * outbox, and that is the whole write. See packages/core/src/rate-machine.ts for the timings.
 */
export function Booth({ songs, onExit, outbox, pending = 0, onTriage }: BoothProps) {
  const [, forceRender] = useState(0);
  const [sync, setSync] = useState<OutboxState>({ pending: 0, failing: false, needsAuth: false, dropped: 0 });

  const byId = useMemo(() => new Map(songs.map((s) => [s.id, s])), [songs]);

  const box = useMemo(
    () => outbox ?? new Outbox({ onChange: setSync }),
    [outbox],
  );

  const machine = useMemo(() => {
    const reduced =
      typeof globalThis.matchMedia === "function" &&
      globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;

    return new RateMachine({
      queue: songs.map((s) => s.id),
      ratings: Object.fromEntries(songs.filter((s) => s.rating !== null).map((s) => [s.id, s.rating!])),
      skipCounts: Object.fromEntries(songs.map((s) => [s.id, s.skipCount])),
      favourites: Object.fromEntries(songs.filter((s) => s.favourite).map((s) => [s.id, true])),
      reduced,
      onChange: () => forceRender((n) => n + 1),
      // What each of these writes lives in ../rate/writes.ts, where it can be tested. It was
      // inline here, and undo was sending a clear for a song that had a previous rating.
      ...boothWrites(box),
    });
    // Rebuilding on a new queue is correct; rebuilding on anything else would lose the sitting.
  }, [songs, box]);

  useEffect(() => () => machine.dispose(), [machine]);

  // The `in` phase paints the incoming card displaced, then settles it on the next frame.
  const phase = machine.phase;
  useEffect(() => {
    if (phase !== "in") return;
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => machine.settle()));
    return () => cancelAnimationFrame(raf);
  }, [phase, machine]);

  useEffect(() => {
    const resume = () => box.resume();
    globalThis.addEventListener?.("online", resume);
    return () => globalThis.removeEventListener?.("online", resume);
  }, [box]);

  const onKey = useCallback(
    (event: KeyboardEvent) => {
      // Never steal a keystroke from a text field.
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;

      const k = event.key;
      if (/^[0-9]$/.test(k)) {
        event.preventDefault();
        machine.rate(k === "0" ? 10 : Number(k));
        return;
      }
      switch (k) {
        case "Escape":
          event.preventDefault();
          onExit();
          return;
        case "s":
        case "S":
          event.preventDefault();
          machine.skip();
          return;
        case "z":
        case "Z":
          event.preventDefault();
          machine.undo();
          return;
        case "ArrowRight":
          event.preventDefault();
          machine.step(1);
          return;
        case "ArrowLeft":
          event.preventDefault();
          machine.step(-1);
          return;
        case "f":
        case "F":
          event.preventDefault();
          machine.toggleFavourite();
          return;
        default:
      }
    },
    [machine, onExit],
  );

  // Before paint: the booth IS the keyboard, and a card that is visible but not yet listening
  // drops the rating typed at it. See the note in Album.tsx.
  useLayoutEffect(() => {
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onKey]);

  const songId = machine.currentSongId;
  const song = songId === null ? null : byId.get(songId);

  /*
   * The rating the undo button would take back, or null when there is nothing to undo. Read from
   * the ratings map rather than from the entry, because the entry stores what the rating WAS and
   * the button names what it IS.
   */
  const lastRated = machine.undoStack.at(-1)?.songId;
  const undoable = lastRated === undefined ? null : (machine.ratings[lastRated] ?? null);
  const value = machine.displayValue;
  const live = machine.isLive;

  if (!song) {
    return (
      <div className={styles.booth}>
        <div className={styles.empty}>
          <div className={styles.emptyTitle}>Nothing left to rate</div>
          <div className={styles.emptyLinks}>
            <button type="button">Random unrated</button>
            <button type="button">By artist</button>
            <button type="button">Oldest added</button>
          </div>
          {/* Nothing left to rate is exactly when the queue is worth mentioning, and the only
              place it is: it must never interrupt a sitting. */}
          {pending > 0 && onTriage && (
            <button type="button" className={styles.emptyTriage} data-testid="empty-triage" onClick={onTriage}>
              {pending.toLocaleString()} songs need a decision →
            </button>
          )}
        </div>
        {legend(onExit)}
      </div>
    );
  }

  const meta = [song.album, song.year, song.genre, duration(song.durationMs)]
    .filter(Boolean)
    .join(" · ")
    .toUpperCase();

  return (
    <div className={styles.booth}>
      <div className={styles.body}>
        {song.art ? (
          <img className={styles.art} data-phase={phase} src={song.art} alt="" />
        ) : (
          <div className={`${styles.art} ${styles.artFallback}`} data-phase={phase}>
            {song.album}
          </div>
        )}

        <div className={styles.card} data-phase={phase}>
          {/* The star is a SIBLING of the title, not inside it: nesting it made the title's own
              text "Sharpest Tool★" — which broke exact matching and, more importantly, is what a
              screen reader would have read out. */}
          <div className={styles.titleRow}>
            <div className={styles.title}>{song.title}</div>
            {machine.isFavourite && (
              <span className={styles.favourite} data-testid="favourite" role="img" aria-label="Favourite">
                ★
              </span>
            )}
          </div>
          <div className={styles.artist}>{song.artist}</div>
          <div className={styles.meta}>{meta}</div>

          {/*
            * Two strips, one rendered at a time. The mobile arrangement is two rows of five
            * 62x56 cells with the numeral inside each — a different SHAPE, not a smaller version,
            * so a media query on the desktop one could not produce it. Both drive the same
            * machine, so the loop is the loop either way.
            */}
          <div className={styles.strip} data-testid="rate-strip">
            <RatingStrip
              value={machine.ratings[song.id] ?? null}
              // Live covers both hovering and the 250ms hold after a keypress: a rating you are
              // making and a rating you have made never look alike.
              live={live ? value : null}
              size="input"
              numerals
              onRate={(n) => machine.rate(n)}
              onHover={(n) => machine.setHover(n)}
              label="Rate this song"
            />
          </div>

          <div className={styles.stripMobile} data-testid="rate-strip-mobile">
            <RatingStrip
              value={machine.ratings[song.id] ?? null}
              live={live ? value : null}
              size="inputMobile"
              onRate={(n) => machine.rate(n)}
              label="Rate this song"
            />
          </div>

          <div className={styles.scoreRow}>
            <div className={styles.score} data-state={live ? "live" : value !== null ? "set" : "unset"}>
              {value ?? "—"}
            </div>
            <div className={styles.previous}>
              {song.rating !== null && song.ratedAt
                ? `PREVIOUSLY ${song.rating} · RATED ${dateLabel(song.ratedAt)}`
                : "NEVER RATED"}
            </div>
          </div>

          {machine.shouldOfferHelp && (
            <div className={styles.help} data-testid="cant-place">
              <b>CAN&rsquo;T PLACE THIS?</b>
              <a
                href={`https://open.spotify.com/search/${encodeURIComponent(`${song.artist} ${song.title}`)}`}
                target="_blank"
                rel="noreferrer"
              >
                Search on Spotify ↗
              </a>
              <button type="button" onClick={() => machine.skip()}>
                Never queue this
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Three states, because they ask for three different things. A queue behind the network
          says keep going; an expired session says stop and sign in; a refused write says something
          was lost — which must never be silent, whatever else is on screen. */}
      {sync.failing && (
        <div className={styles.syncError}>{sync.needsAuth ? OUTBOX_AUTH_ERROR : OUTBOX_ERROR}</div>
      )}
      {sync.dropped > 0 && (
        <div className={styles.syncError} data-testid="outbox-dropped">
          {outboxDroppedMessage(sync.dropped)}
        </div>
      )}

      {/*
        * Mobile's three actions, 48px tall. On desktop these are keys and the legend says so;
        * a phone has no S, Z or F, and an action nobody can reach is not an action.
        */}
      <div className={styles.mobileActions} data-testid="mobile-actions">
        <button type="button" className={styles.mobileAction} onClick={() => machine.skip()}>
          SKIP
        </button>
        <button
          type="button"
          className={styles.mobileAction}
          data-live={undoable !== null ? "true" : undefined}
          data-testid="mobile-undo"
          disabled={undoable === null}
          onClick={() => machine.undo()}
        >
          {/* `UNDO 8` right after a rating: the number is what pressing it takes back. */}
          {undoable === null ? "UNDO" : `UNDO ${undoable}`}
        </button>
        <button
          type="button"
          className={`${styles.mobileAction} ${styles.mobileActionPrimary}`}
          aria-pressed={machine.isFavourite}
          onClick={() => machine.toggleFavourite()}
        >
          FAVOURITE
        </button>
      </div>

      {legend(onExit)}
    </div>
  );
}

function legend(onExit: () => void) {
  return (
    <div className={styles.legend}>
      <div>1–0 RATE</div>
      <div>S SKIP</div>
      <div>Z UNDO</div>
      <div>← → MOVE</div>
      <div data-testid="legend-favourite">F FAVOURITE</div>
      <button type="button" onClick={onExit} style={{ font: "inherit", color: "inherit", cursor: "pointer" }}>
        ESC BACK TO THE LIST
      </button>
    </div>
  );
}

/** The top-bar context the shell renders while the booth is open. */
export function BoothContext({ album, position, total }: { album: string; position: number; total: number }) {
  return (
    <>
      <span className={styles.modeChip}>RATING MODE · ESC EXITS</span>
      <span className={styles.context}>{album}</span>
      <span className={styles.queuePos}>
        {position} / {total}
      </span>
    </>
  );
}
